import React, { useEffect, useState } from 'react';
import { Card, Form, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMoneyBillWave,
    faSave,
    faInfoCircle,
    faTrash,
    faUndo,
    faClock
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useFormaPagamento, useAlterarFormaPagamento, useContasFormaPagamento } from 'hooks/useFormaPagamento';
import { AlterarFormaPagamentoDto } from 'services/formaPagamentoService';
import { tiposFormaPagamento, tipoStringToNumber } from 'data/formasPagamento';
import PageBreadcrumb from "components/common/PageBreadcrumb";
import { useToast } from "providers/ToastProvider";
import BotaoExcluirFormaPagamento from "components/formaPagamento/BotaoExcluirFormaPagamento";
import BotaoReativarFormaPagamento from "components/formaPagamento/BotaoReativarFormaPagamento";
import SeletorPrazos from "components/prazo/SeletorPrazos";


// Define breadcrumbs for the edit forma pagamento page
const formaPagamentoBreadcrumbItems = (formaPagamento?: any) => [
    {
        label: "Retaguarda",
        url: "#!",
    },
    {
        label: "Cadastros",
        url: "#!",
    },
    {
        label: "Formas de Pagamento",
        url: "/cadastros/formas-pagamento",
    },
    {
        label: formaPagamento ? `Editar #${formaPagamento.codigo}` : "Editar",
        active: true,
    },
];

const EditarFormaPagamento: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { showToast } = useToast();

    // React Hook Form setup
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<AlterarFormaPagamentoDto>({
        defaultValues: {
            descricao: '',
            tipo: 0,
            contaDestinoId: undefined,
        },
        mode: 'onSubmit',
    });

    // Estado para prazos selecionados
    const [prazosIds, setPrazosIds] = useState<string[]>([]);

    // Hooks
    const formaPagamentoQuery = useFormaPagamento(id!, !!id);
    const alterarMutation = useAlterarFormaPagamento();
    const contasQuery = useContasFormaPagamento();

    const formaPagamento = formaPagamentoQuery.data;

    // Carregar dados quando disponíveis - aguardar contas carregarem para evitar race condition
    useEffect(() => {
        if (formaPagamento && !contasQuery.isLoading) {
            reset({
                descricao: formaPagamento.descricao,
                tipo: tipoStringToNumber[formaPagamento.tipo] ?? 0,
                contaDestinoId: formaPagamento.contaDestinoId,
                movimentaCaixa: formaPagamento.movimentaCaixa,
            });

            // Carregar prazos selecionados
            const prazosIdsAtivos = formaPagamento.prazos
                ?.filter(p => !p.isDeleted)
                ?.map(p => p.id) || [];
            setPrazosIds(prazosIdsAtivos);
        }
    }, [formaPagamento, contasQuery.isLoading, reset]);

    const onSubmit = async (data: AlterarFormaPagamentoDto) => {
        try {
            const payload = {
                ...data,
                contaDestinoId: data.contaDestinoId || undefined,
                prazosIds: prazosIds.length > 0 ? prazosIds : undefined,
            };
            await alterarMutation.mutateAsync({
                id: id!,
                data: payload
            });

            // Navigate with success message
            navigate('/cadastros/formas-pagamento');
            showToast('Forma de pagamento atualizada com sucesso', 'success');
        } catch (error) {
            console.error('Erro ao alterar forma de pagamento:', error);
        }
    };

    if (formaPagamentoQuery.isLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                </Spinner>
            </div>
        );
    }

    if (formaPagamentoQuery.isError || !formaPagamento) {
        return (
            <div>
                <Alert variant="danger" className="border border-danger border-opacity-25 bg-danger-subtle">
                    <div className="d-flex align-items-center text-danger-emphasis">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                        Forma de pagamento não encontrada.
                    </div>
                </Alert>
            </div>
        );
    }

    return (
        <div>
            <PageBreadcrumb items={formaPagamentoBreadcrumbItems(formaPagamento)} />

            <Form
                noValidate
                className="mb-9"
                onSubmit={handleSubmit(onSubmit)}
            >
                <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
                    <div>
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <h2 className="mb-0">Editar Forma de Pagamento #{formaPagamento.codigo}</h2>
                            <Badge bg={formaPagamento.isDeleted ? 'danger' : 'success'}>
                                {formaPagamento.isDeleted ? 'Inativa' : 'Ativa'}
                            </Badge>
                        </div>
                        <h5 className="text-body-tertiary fw-semibold">
                            Altere as informações da forma de pagamento
                        </h5>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                        {formaPagamento.isDeleted ? (
                            <BotaoReativarFormaPagamento
                                formaPagamento={formaPagamento}
                                variant="success"
                            />
                        ) : (
                            <BotaoExcluirFormaPagamento
                                formaPagamento={formaPagamento}
                                variant="danger"
                            />
                        )}
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={alterarMutation.isPending}
                        >
                            <FontAwesomeIcon icon={faSave} className="me-2" />
                            {alterarMutation.isPending ? "Salvando..." : "Salvar Alterações"}
                        </Button>
                    </div>
                </div>

                {/* Informações Básicas Card */}
                <Card className="mb-4">
                    <Card.Body>
                        <h5 className="mb-3">
                            <FontAwesomeIcon icon={faMoneyBillWave} className="me-2" />
                            Informações Básicas
                        </h5>

                        {alterarMutation.isError && (
                            <Alert variant="danger" className="border border-danger border-opacity-25 bg-danger-subtle mb-4">
                                <div className="text-danger-emphasis">
                                    <strong>Erro:</strong> Não foi possível alterar a forma de pagamento.
                                    Tente novamente.
                                </div>
                            </Alert>
                        )}

                        <div className="row g-3">
                            {/* Código (readonly) */}
                            <div className="col-md-2">
                                <Form.Group>
                                    <Form.Label className="text-body-emphasis fw-semibold">
                                        Código
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={`#${formaPagamento.codigo}`}
                                        readOnly
                                        className="bg-body-secondary"
                                    />
                                    <Form.Text className="text-body-secondary">
                                        Código identificador único
                                    </Form.Text>
                                </Form.Group>
                            </div>

                            {/* Descrição */}
                            <div className="col-md-10">
                                <Form.Group>
                                    <Form.Label className="text-body-emphasis fw-semibold">
                                        Descrição *
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        {...register('descricao', {
                                            required: 'Descrição é obrigatória',
                                            maxLength: {
                                                value: 100,
                                                message: 'Descrição deve ter no máximo 100 caracteres'
                                            },
                                            validate: (value) => {
                                                if (!value?.trim()) {
                                                    return 'Descrição não pode estar vazia';
                                                }
                                                return true;
                                            }
                                        })}
                                        isInvalid={!!errors.descricao}
                                        placeholder="Ex: Dinheiro, Cartão de Crédito, PIX..."
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.descricao?.message}
                                    </Form.Control.Feedback>
                                    <Form.Text className="text-body-secondary">
                                        Nome que identificará esta forma de pagamento
                                    </Form.Text>
                                </Form.Group>
                            </div>

                            {/* Tipo */}
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="text-body-emphasis fw-semibold">
                                        Tipo *
                                    </Form.Label>
                                    <Form.Select
                                        {...register('tipo', {
                                            required: 'Tipo é obrigatório',
                                            setValueAs: (value) => value === '' ? undefined : Number(value)
                                        })}
                                        isInvalid={!!errors.tipo}
                                    >
                                        {tiposFormaPagamento.map(tipo => (
                                            <option key={tipo.value} value={tipo.value}>
                                                {tipo.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.tipo?.message}
                                    </Form.Control.Feedback>
                                    <Form.Text className="text-body-secondary">
                                        Categoria da forma de pagamento
                                    </Form.Text>
                                </Form.Group>
                            </div>

                            {/* Movimenta Caixa */}
                            <div className="col-12">
                                <Form.Check
                                    type="switch"
                                    id="movimentaCaixa"
                                    label="Movimenta Caixa"
                                    {...register('movimentaCaixa')}
                                />
                                <Form.Text className="text-body-secondary">
                                    Quando ativo, pagamentos com esta forma geram movimento no caixa do PDV.
                                </Form.Text>
                            </div>

                            {/* Conta Destino */}
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="text-body-emphasis fw-semibold">
                                        Conta Destino (Opcional)
                                    </Form.Label>
                                    <Form.Select
                                        {...register('contaDestinoId', {
                                            setValueAs: (value) => value === '' ? undefined : value
                                        })}
                                        disabled={contasQuery.isLoading}
                                    >
                                        <option value="">Selecione uma conta (opcional)</option>
                                        {contasQuery.data?.map((conta: any) => (
                                            <option key={conta.id} value={conta.id}>
                                                {conta.nome}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    {contasQuery.isLoading && (
                                        <Form.Text className="text-body-secondary">
                                            <Spinner size="sm" className="me-1" />
                                            Carregando contas...
                                        </Form.Text>
                                    )}
                                    {!contasQuery.isLoading && (
                                        <Form.Text className="text-body-secondary">
                                            Conta para onde o valor será depositado ao fechar o caixa.
                                            Para dinheiro, será usado também para sangrias e suprimentos.
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                {/* Prazos Card */}
                <Card className="mb-4">
                    <Card.Body>
                        <h5 className="mb-3">
                            <FontAwesomeIcon icon={faClock} className="me-2" />
                            Prazos de Pagamento
                        </h5>

                        <SeletorPrazos
                            prazosIds={prazosIds}
                            onPrazosChange={setPrazosIds}
                            disabled={alterarMutation.isPending}
                        />
                    </Card.Body>
                </Card>

                <div className="d-flex justify-content-between mt-4">
                    <Button
                        variant="secondary"
                        type="button"
                        onClick={() => navigate("/cadastros/formas-pagamento")}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={alterarMutation.isPending}
                    >
                        <FontAwesomeIcon icon={faSave} className="me-2" />
                        {alterarMutation.isPending ? "Salvando..." : "Salvar Alterações"}
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default EditarFormaPagamento; 